import React, { useEffect, useState, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Container,
  Stack,
  Typography,
  Button,
  Grid,
  Paper,
  Link,
  Divider,
  Avatar,
  IconButton,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import action1 from '../assets/img/action1.png';
import action2 from '../assets/img/action2.png';
import action3 from '../assets/img/action3.png';
import { LandingLayout } from '../components/LandingLayout';

const brand = {
  primary: '#800b33',
  dark: '#5c0620',
  tint: '#ffe9ef',
  slate: '#1d1320',
  soft: '#fdf1f4',
};

const heroSlides = [
  'https://images.unsplash.com/photo-1488722796624-0aa6f1bb6399?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=900&q=80',
];

const AnimatedTypography = ({ children, sx, ...props }) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <Typography
      ref={elementRef}
      sx={{
        ...sx,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
      }}
      {...props}
    >
      {children}
    </Typography>
  );
};

const ImageCarousel = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % heroSlides.length);
    }, 3200);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box
      sx={{
        borderRadius: 4,
        background: '#fff',
        boxShadow: '0 25px 60px rgba(0,0,0,0.15)',
        padding: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{
          overflow: 'hidden',
          borderRadius: 38,
          background: '#0f172a',
          padding: 16,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            borderRadius: 26,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundImage: `url(${heroSlides[current]})`,
            height: { xs: 320, sm: 360 },
            transition: 'background-image 0.7s ease-in-out',
            boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: -10,
            border: '2px solid rgba(255,255,255,0.25)',
            borderRadius: 40,
            pointerEvents: 'none',
            opacity: 0.5,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 160,
            height: 4,
            borderRadius: 2,
            background: 'rgba(255,255,255,0.5)',
          }}
        />
      </Box>
    </Box>
  );
};
const Hero = () => {
  const { t } = useTranslation();
  return (
  <Box
    sx={{
      py: { xs: 8, md: 14 },
      position: 'relative',
      overflow: 'hidden',
      background: 'radial-gradient(circle at 10% 20%, rgba(128, 11, 51, 0.03) 0%, transparent 60%), radial-gradient(circle at 90% 80%, rgba(59, 130, 246, 0.03) 0%, transparent 60%)'
    }}
  >
    {/* Background Decorative Mesh */}
    <Box sx={{ position: 'absolute', top: -100, right: -100, width: 600, height: 600, background: 'radial-gradient(circle, rgba(128,11,51,0.08) 0%, transparent 70%)', filter: 'blur(80px)', zIndex: -1, animation: 'pulse 8s infinite ease-in-out' }} />

    <Container maxWidth="lg">
      <Grid container spacing={8} alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography variant="h1" sx={{ fontWeight: 900, color: brand.slate, lineHeight: 1.1, fontSize: { xs: '2.5rem', md: '4rem' }, letterSpacing: '-0.02em' }}>
            {t('hero.title_prefix', "Master your group's")} <span style={{ color: brand.primary, position: 'relative' }}>{t('hero.title_highlight', "wealth & trust.")}<Box component="svg" sx={{ position: 'absolute', bottom: -6, left: 0, width: '100%', height: 8, zIndex: -1 }} viewBox="0 0 100 10" preserveAspectRatio="none"><path d="M0 5 Q 50 10 100 5" stroke={brand.tint} strokeWidth="8" fill="none" opacity="0.6" /></Box></span>
          </Typography>

          <Typography variant="body1" sx={{ color: '#4c3a46', mt: 3, mb: 5, fontSize: '1.25rem', lineHeight: 1.6, maxWidth: 480 }}>
            {t('hero.subtitle', "Automate collections, streamlined loans, and crystal-clear governance. The modern way to grow together.")}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              size="large"
              sx={{
                bgcolor: brand.primary,
                textTransform: 'none',
                px: 5,
                py: 1.8,
                borderRadius: 4,
                fontSize: '1.1rem',
                fontWeight: 700,
                boxShadow: '0 20px 40px -10px rgba(128,11,51,0.3)',
                '&:hover': { bgcolor: brand.dark, transform: 'translateY(-2px)' },
                color: '#fff',
                transition: 'all 0.2s ease'
              }}
              endIcon={<ArrowForwardIcon />}
            >
              {t('hero.cta_start', "Start now")}
            </Button>
            <Button
              component="a"
              href="mailto:hello@savvy-community.com"
              variant="outlined"
              size="large"
              sx={{
                color: brand.slate,
                borderColor: '#e2e8f0',
                textTransform: 'none',
                px: 5,
                py: 1.8,
                borderRadius: 4,
                fontSize: '1.1rem',
                fontWeight: 700,
                '&:hover': { borderColor: brand.primary, color: brand.primary, bgcolor: 'transparent' },
              }}
            >
              {t('hero.cta_demo', "Request demo")}
            </Button>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 5 }}>
            <Avatar
              src="https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=100&q=80"
              sx={{ width: 48, height: 48, border: '2px solid white', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            />
            <Box>
              <Typography variant="caption" sx={{ display: 'block', color: brand.muted, fontWeight: 600 }}>{t('hero.trusted_label', "Trusted by champions like")}</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: brand.slate }}>{t('hero.trusted_name', "Glory, Umoja SACCO Chairperson")}</Typography>
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={12} md={6} sx={{ position: 'relative' }}>
          {/* Glassmorphic Container for Dashboard */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 6,
              p: 0,
              background: 'rgba(255,255,255,0.7)',
              backdropFilter: 'blur(20px)',
              position: 'relative',
              overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.8)',
              boxShadow: '0 50px 100px -20px rgba(50, 50, 93, 0.15), 0 30px 60px -30px rgba(0, 0, 0, 0.2)',
              transform: 'perspective(1000px) rotateY(-5deg) rotateX(2deg)',
              transition: 'transform 0.5s ease',
              '&:hover': { transform: 'perspective(1000px) rotateY(0deg) rotateX(0deg)' }
            }}
          >
            {/* Subtler inner gradient */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 100%)',
                pointerEvents: 'none',
              }}
            />

            <Grid container sx={{ position: 'relative' }}>
              {/* Sidebar / Left Panel */}
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    height: '100%',
                    p: 4,
                    pt: 6,
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.8) 0%, rgba(248,250,252,0.8) 100%)', // Light Glass
                    borderRight: '1px solid rgba(0,0,0,0.05)',
                    color: brand.slate,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: 4,
                  }}
                >
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e', boxShadow: '0 0 10px #22c55e' }} />
                      <Typography variant="overline" sx={{ letterSpacing: 2, fontWeight: 700, color: brand.primary, opacity: 0.8 }}>{t('hero.dashboard.mission_control', "Mission Control")}</Typography>
                    </Stack>
                    <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', color: brand.slate }}>
                      {t('hero.dashboard.weekly', "Weekly")}<br />{t('hero.dashboard.momentum', "Momentum")}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2, color: brand.muted, lineHeight: 1.6 }}>
                      {t('hero.dashboard.description', "Real-time pulse across savings, loans, and member engagement.")}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: brand.muted, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                      {t('hero.dashboard.health_score', "Health Score")}
                    </Typography>
                    <Typography variant="h2" sx={{ fontWeight: 800, mt: 0.5, color: brand.slate }}>
                      98<span style={{ fontSize: '0.5em', color: brand.muted }}>%</span>
                    </Typography>
                    <Box sx={{ display: 'inline-flex', mt: 1, px: 1, py: 0.5, bgcolor: '#dcfce7', borderRadius: 1, color: '#166534', fontSize: 12, fontWeight: 700 }}>
                      +12% vs last cycle
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={7}>
                <Box sx={{ p: 4, pt: 6 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: brand.slate }}>
                      {t('hero.dashboard.performance', "Performance")}
                    </Typography>
                    <Box sx={{ px: 1.5, py: 0.5, bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, borderRadius: 1, fontSize: '0.75rem' }}>
                      {t('hero.dashboard.on_track', "On track")}
                    </Box>
                  </Stack>

                  <Stack spacing={3}>
                    {[
                      { label: t('hero.dashboard.collections', "Collections"), value: '1.4x', note: t('hero.dashboard.above_target', "Above target"), width: '82%', color: brand.primary },
                      { label: t('hero.dashboard.loan_approvals', "Loan approvals"), value: '92%', note: t('hero.dashboard.stable', "Stable"), width: '68%', color: '#3b82f6' },
                      { label: t('hero.dashboard.member_growth', "Member growth"), value: '+24', note: t('hero.dashboard.new_record', "New record"), width: '95%', color: '#f59e0b' },
                    ].map((stat) => (
                      <Box key={stat.label}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: brand.slate }}>{stat.label}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: brand.slate }}>{stat.value}</Typography>
                        </Stack>
                        <Box sx={{ height: 6, borderRadius: 3, bgcolor: '#f1f5f9', overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: stat.width, bgcolor: stat.color, borderRadius: 3 }} />
                        </Box>
                        <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>{stat.note}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Floating Decorative Element - Premium Coin */}
          <Paper sx={{
            position: 'absolute',
            top: -20, right: -20,
            width: 64, height: 64,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)',
            boxShadow: '0 20px 40px rgba(245, 158, 11, 0.4)',
            display: 'grid', placeItems: 'center',
            animation: 'float 6s ease-in-out infinite'
          }}>
            <MonetizationOnIcon sx={{ color: 'white', fontSize: 32 }} />
          </Paper>
        </Grid>
      </Grid>
    </Container>
    <style>
      {`
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
            100% { transform: translateY(0px); }
          }
           @keyframes pulse {
            0% { transform: scale(1); opacity: 0.5; }
            50% { transform: scale(1.1); opacity: 0.7; }
            100% { transform: scale(1); opacity: 0.5; }
          }
        `}
    </style>
  </Box>
  );
};

const Stats = () => {
  const { t } = useTranslation();
  const stats = [
    { label: t('stats.groups', 'Groups supported'), value: '520+' },
    { label: t('stats.growth', 'Avg. savings growth'), value: '28%' },
    { label: t('stats.retention', 'Member retention'), value: '94%' }
  ];

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Paper elevation={2} sx={{ borderRadius: 4, p: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
          {stats.map((item) => (
            <Box key={item.label} sx={{ minWidth: 160 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: brand.primary }}>{item.value}</Typography>
              <Typography variant="body2" sx={{ color: '#6b6470' }}>{item.label}</Typography>
            </Box>
          ))}
        </Paper>
      </Container>
    </Box>
  );
};

const GalleryCarousel = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState(1);

  const galleryCards = [
    {
      title: t('gallery.reports.title', 'Run Reports & Statements'),
      subtitle: t('gallery.reports.subtitle', 'Generate audit-ready PDFs so members always see where their savings sit.'),
      image: action1,
    },
    {
      title: t('gallery.savings.title', 'Manage Savings & Contributions'),
      subtitle: t('gallery.savings.subtitle', 'Oversee every group contribution, emergency fund, and penalty with confidence.'),
      image: action2,
    },
    {
      title: t('gallery.loans.title', 'Streamline Loan Approvals'),
      subtitle: t('gallery.loans.subtitle', 'Securely route every request from chairperson to secretary to repayment.'),
      image: action3,
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % galleryCards.length);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  const handleShift = (direction) => {
    setActive((prev) => (prev + direction + galleryCards.length) % galleryCards.length);
  };

  return (
    <Box sx={{ py: 6 }}>
      <Container maxWidth="lg">
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={5}>
          <AnimatedTypography
            variant="h5"
            sx={{
              fontWeight: 800,
              position: 'relative',
              display: 'inline-block',
              '&::after': {
                content: '""',
                position: 'absolute',
                width: 60,
                height: 4,
                bottom: -12,
                left: 0,
                borderRadius: 2,
                bgcolor: brand.primary,
              },
            }}
          >
            See Savvy Community in action
          </AnimatedTypography>
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              onClick={() => handleShift(-1)}
              sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0xs,0.12)', color: brand.slate }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleShift(1)}
              sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.12)', color: brand.slate }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Stack>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-end',
            overflow: 'visible',
            gap: { xs: 0.5, md: 1 },
            px: { xs: 1, md: 0 },
            height: 480,
          }}
        >
          {[-1, 0, 1].map((offset) => {
            const index = (active + offset + galleryCards.length) % galleryCards.length;
            const card = galleryCards[index];
            const isCenter = offset === 0;
            const sizeProps = {
              width: isCenter ? { xs: 240, md: 320 } : { xs: 200, md: 260 },
              height: isCenter ? { xs: 340, md: 430 } : { xs: 300, md: 370 },
            };

            return (
              <Box
                key={card.title}
                onClick={() => setActive(index)}
                sx={{
                  flex: '0 0 auto',
                  cursor: 'pointer',
                  zIndex: isCenter ? 3 : 1,
                  transition: 'transform 0.65s cubic-bezier(0.25, 1, 0.35, 1), opacity 0.65s ease',
                  transform: isCenter
                    ? 'translateY(0)'
                    : offset === -1
                      ? 'translate(-34%, 18px) scale(0.94)'
                      : 'translate(34%, 18px) scale(0.94)',
                  opacity: isCenter ? 1 : 0.8,
                }}
              >
                <Paper
                  elevation={isCenter ? 6 : 3}
                  sx={{
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: isCenter
                      ? '0 35px 80px rgba(0,0,0,0.28)'
                      : '0 20px 35px rgba(0,0,0,0.18)',
                    width: sizeProps.width,
                    height: sizeProps.height,
                  }}
                >
                  <Box
                    component="img"
                    src={card.image}
                    alt={card.title}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Paper>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

const FeatureTiles = () => {
  const { t } = useTranslation();
  const features = [
    {
      title: t('features.collections.title', 'Always-on collections'),
      body: t('features.collections.body', 'Automated reminders, receipts, and reconciliation cover every group cycle.'),
      icon: <MonetizationOnIcon fontSize="large" color="inherit" />,
    },
    {
      title: t('features.governance.title', 'Secure governance'),
      body: t('features.governance.body', 'Role-based access, audit logs, and alerts keep decisions traceable.'),
      icon: <SecurityIcon fontSize="large" color="inherit" />,
    },
    {
      title: t('features.delight.title', 'Member delight'),
      body: t('features.delight.body', 'Personalized dashboards and engagements keep everyone in sync.'),
      icon: <LoyaltyIcon fontSize="large" color="inherit" />,
    },
  ];

  return (
    <Box sx={{ py: 10 }} id="product">
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {features.map((feature) => (
            <Grid item xs={12} md={4} key={feature.title}>
              <Paper elevation={0} sx={{ borderRadius: 3, p: 4, border: '1px solid rgba(128,11,51,0.1)', minHeight: 220, display: 'flex', gap: 2, flexDirection: 'column' }}>
                <Box sx={{ color: brand.primary }}>{feature.icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{feature.title}</Typography>
                <Typography variant="body2" sx={{ color: '#6b6470' }}>{feature.body}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

const Steps = () => {
  const { t } = useTranslation();
  const steps = [
    { title: t('steps.invite.title', 'Invite leaders'), detail: t('steps.invite.detail', 'Add admins with fine-grained roles and approvals.') },
    { title: t('steps.orchestrate.title', 'Orchestrate contributions'), detail: t('steps.orchestrate.detail', 'Schedule savings, penalties, and dues in minutes.') },
    { title: t('steps.analyze.title', 'Analyze impact'), detail: t('steps.analyze.detail', 'Live metrics show who is growing and where to coach.') },
  ];

  return (
    <Box sx={{ py: 8, background: '#fff5f7' }} id="solutions">
      <Container maxWidth="lg">
        <AnimatedTypography
          variant="h5"
          sx={{
            fontWeight: 800,
            mb: 4,
            position: 'relative',
            display: 'inline-block',
            '&::after': {
              content: '""',
              position: 'absolute',
              width: 60,
              height: 4,
              bottom: -12,
              left: 0,
              borderRadius: 2,
              bgcolor: brand.primary,
            },
          }}
        >
          How it works
        </AnimatedTypography>
        <Grid container spacing={4}>
          {steps.map((step, index) => (
            <Grid item xs={12} md={4} key={step.title}>
              <Paper
                elevation={4}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  border: '1px solid rgba(128,11,51,0.1)',
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,233,239,0.6))',
                  minHeight: 220,
                }}
              >
                <Typography variant="subtitle2" sx={{ color: brand.primary, fontWeight: 700 }}>0{index + 1}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>{step.title}</Typography>
                <Typography variant="body2" sx={{ color: '#6b6470', mt: 1 }}>{step.detail}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

const Testimonials = () => {
  const { t } = useTranslation();
  const testimonials = [
    {
      quote: t('testimonials.glory.quote', 'Savvy Community replaced four spreadsheets and finally gave our board confidence.'),
      author: t('testimonials.glory.author', 'Glory, Chairperson at Umoja SACCO'),
      avatar: 'https://ui-avatars.com/api/?name=Glory+Chamber&background=800b33&color=fff',
    },
    {
      quote: t('testimonials.juma.quote', 'Members now see where their contributions go and trust the process.'),
      author: t('testimonials.juma.author', 'Juma, Treasurer'),
      avatar: 'https://ui-avatars.com/api/?name=Juma+Temba&background=800b33&color=fff',
    },
  ];

  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="lg">
        <AnimatedTypography
          variant="h5"
          sx={{
            fontWeight: 800,
            mb: 4,
            position: 'relative',
            display: 'inline-block',
            '&::after': {
              content: '""',
              position: 'absolute',
              width: 60,
              height: 4,
              bottom: -12,
              left: 0,
              borderRadius: 2,
              bgcolor: brand.primary,
            },
          }}
        >
          Trusted by leaders
        </AnimatedTypography>
        <Grid container spacing={4}>
          {testimonials.map((item) => (
            <Grid item xs={12} md={6} key={item.author}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  minHeight: 220,
                  background: 'linear-gradient(135deg, #fff, #ffeeef)',
                  border: '1px solid rgba(128,11,51,0.18)',
                }}
              >
                <Typography variant="body1" sx={{ fontStyle: 'italic', color: '#3a2633' }}>
                  “{item.quote}”
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 3 }}>
                  <Avatar
                    src={item.avatar}
                    alt={item.author}
                    sx={{ width: 40, height: 40, border: '2px solid rgba(128,11,51,0.3)' }}
                  />
                  <Typography variant="subtitle2" sx={{ color: '#6b6470', fontWeight: 700 }}>
                    {item.author}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default function LandingPage() {
  return (
    <LandingLayout>
      <Hero />
      <GalleryCarousel />
      <Stats />
      <FeatureTiles />
      <Steps />
      <Testimonials />
    </LandingLayout>
  );
}
